import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import GeoArt from "../components/GeoArt";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const CHARACTERS = ["Joy", "Witness", "Heavy Crown", "Static", "Soft Landing", "Inheritance", "Signal", "Ember"];

const PROCESS = [
  { no: "01", title: "Listen", body: "Every face starts with a story — from the block, the family table, the timeline, the archive." },
  { no: "02", title: "Sketch Loud", body: "Fast, public, unpolished. Street art energy and cartoon grammar before the canvas ever sees paint." },
  { no: "03", title: "Build the Field", body: "Layers of acrylic, aerosol, and collage become the world the character must move through." },
  { no: "04", title: "Let the Face Emerge", body: "The identity surfaces last — eyes, mouth, crown — carrying everything the field demanded." },
];

export default function FieldFaces() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="pt-24 sm:pt-32" data-testid="field-faces-page">
      <section className="container-x section-y relative">
        <p className="eyebrow mb-6" data-testid="ff-eyebrow">Signature Series</p>
        <MaskedLines lines={["Field", "Faces"]} className="h-display !text-6xl sm:!text-8xl lg:!text-9xl text-[#FF1A1A]" />
        <Reveal delay={0.35}>
          <p className="mt-10 max-w-2xl text-muted-foreground" data-testid="ff-intro">
            Field Faces is a collection of characters representing identity, memory, emotions, resilience, joy, and transformation within the Black experience in America.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-border relative overflow-hidden" ref={ref}>
        <div className="container-x section-y grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <p className="font-mono-x text-xs text-[#FF1A1A] mb-4">CHAPTER 01</p>
              <h2 className="h-section mb-6" data-testid="ff-field-title">The Field</h2>
              <p className="text-muted-foreground max-w-md">
                The Field represents real-world challenges and lived experiences — the ground every character walks. It is history, pressure, neighborhood, and noise. It shapes everything, and it never gets the final word.
              </p>
            </div>
          </Reveal>
          <motion.div style={{ y }} className="border border-border p-4 rotate-1">
            <GeoArt seed="ff-the-field" className="w-full" />
          </motion.div>
        </div>
        <div className="container-x section-y grid grid-cols-1 lg:grid-cols-2 gap-12 items-center border-t border-border">
          <motion.div style={{ y }} className="border border-border p-4 -rotate-1 order-2 lg:order-1">
            <GeoArt seed="ff-the-faces" className="w-full" />
          </motion.div>
          <Reveal className="order-1 lg:order-2">
            <div>
              <p className="font-mono-x text-xs text-[#FF1A1A] mb-4">CHAPTER 02</p>
              <h2 className="h-section mb-6" data-testid="ff-faces-title">The Faces</h2>
              <p className="text-muted-foreground max-w-md">
                The Faces represent the identities and emotions people carry through life — the mask, the grin, the side-eye, the joy that refuses to be scheduled. Each face is a biography told in geometry.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="Character Gallery" title="Meet the Cast" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {CHARACTERS.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <div className="group border border-border overflow-hidden" data-testid={`character-card-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="transition-transform duration-700 group-hover:scale-105">
                    <GeoArt seed={`char-${c}`} ratio="1/1" className="w-full" />
                  </div>
                  <div className="p-4 border-t border-border flex justify-between items-center">
                    <span className="font-display font-bold uppercase text-sm">{c}</span>
                    <span className="font-mono-x text-[10px] text-muted-foreground">FF-{String(i + 1).padStart(2, "0")}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="Studio Process" title="How a Face Is Born" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {PROCESS.map((s, i) => (
              <Reveal key={s.no} delay={i * 0.08} className="bg-card">
                <div className="p-8 h-full" data-testid={`process-step-${s.no}`}>
                  <span className="font-mono-x text-xs text-[#FF1A1A]">{s.no}</span>
                  <h3 className="font-display font-extrabold uppercase text-lg mt-3 mb-3">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link to="/artwork" className="btn-red" data-testid="ff-view-works">View Field Faces Works <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
